import { ArrowUpRight, ArrowLeft, Check, Clock, MapPin, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { CONTACT, MEDIA, buildPaypalUrl } from "../i18n";
import Reveal from "./Reveal";

function PaypalLink({ amount, item, label, testId }) {
  const returnUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/grupal?paid=true`
      : "";
  const href = buildPaypalUrl(returnUrl, { amount, itemName: item });
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="btn-accent w-full justify-center"
      data-testid={testId}
    >
      {label}
      <ArrowUpRight size={16} />
    </a>
  );
}

function WhatsappLink({ message, label, testId, variant = "primary" }) {
  const url = `https://wa.me/${CONTACT.phoneClean}?text=${encodeURIComponent(message)}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={`${variant === "primary" ? "btn-accent" : "btn-primary"} w-full justify-center`}
      data-testid={testId}
    >
      <MessageCircle size={16} />
      {label}
    </a>
  );
}

function MetaLine({ icon, value }) {
  return (
    <div className="flex items-center gap-3 text-[#5E554C]">
      <span className="text-[#1A9BC1]">{icon}</span>
      <span className="text-base font-light">{value}</span>
    </div>
  );
}

export default function GroupActivities({ t }) {
  const w = t.group.workshop;

  return (
    <div className="App" data-testid="group-page">
      {/* Hero */}
      <section
        className="relative pt-40 pb-20 md:pt-48 md:pb-28 bg-[#FDFBF7] overflow-hidden bg-grain"
        data-testid="group-hero"
      >
        <div
          aria-hidden
          className="absolute -right-20 -top-32 font-chinese text-[28rem] leading-none text-[#F5E6C8] select-none pointer-events-none hidden md:block"
        >
          源
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm tracking-widest uppercase text-[#8D7B68] hover:text-[#1A9BC1] transition-colors mb-12"
                data-testid="group-back-home"
              >
                <ArrowLeft size={14} />
                {t.group.backHome}
              </Link>
            </Reveal>
            <Reveal delay={120}>
              <span className="overline">{t.group.heroOverline}</span>
            </Reveal>
            <Reveal delay={200}>
              <h1
                className="font-serif text-5xl md:text-7xl lg:text-8xl mt-6 leading-[0.98] tracking-tight text-[#3E362E] whitespace-pre-line"
                data-testid="group-hero-title"
              >
                {t.group.heroTitle}
              </h1>
            </Reveal>
            <Reveal delay={320}>
              <p className="mt-10 text-[#5E554C] text-lg md:text-xl font-light leading-relaxed max-w-2xl">
                {t.group.heroBody}
              </p>
            </Reveal>
            <Reveal delay={420}>
              <p className="mt-6 font-serif italic text-[#1A9BC1] text-lg">
                {t.group.heroNote}
              </p>
            </Reveal>
          </div>

          <Reveal delay={500} className="lg:col-span-5">
            <div className="img-frame aspect-[4/5] hidden lg:block">
              <img src={MEDIA.candlelight} alt="Sacred space candlelight" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Workshop block */}
      <section
        id="taller"
        data-testid="workshop-section"
        className="py-24 md:py-36 bg-[#FDFBF7]"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-7">
              <Reveal>
                <span className="section-number">{w.tag}</span>
              </Reveal>
              <Reveal delay={120}>
                <p className="overline mt-3">{w.overline}</p>
              </Reveal>
              <Reveal delay={200}>
                <h2
                  className="font-serif text-4xl md:text-5xl lg:text-6xl mt-5 leading-[1.05] text-[#3E362E]"
                  data-testid="workshop-title"
                >
                  {w.title}
                </h2>
              </Reveal>
              <Reveal delay={280}>
                <p className="font-serif italic text-xl md:text-2xl text-[#1A9BC1] mt-4">
                  {w.subtitle}
                </p>
              </Reveal>
              <Reveal delay={380}>
                <p className="mt-8 text-[#5E554C] text-lg font-light leading-relaxed max-w-2xl">
                  {w.kicker}
                </p>
              </Reveal>
              <Reveal delay={460}>
                <div className="mt-10 img-frame aspect-[16/10] max-w-2xl">
                  <img src={MEDIA.pastLives} alt="Cosmic horizon · echoes of past lives" />
                </div>
              </Reveal>
            </div>

            <Reveal delay={300} className="lg:col-span-5">
              <div className="bg-[#F5EFE6] border border-[#E8E1D9] p-7 md:p-9 h-full flex flex-col gap-6">
                {w.nextDate && (
                  <div>
                    <span className="overline">{w.nextDateLabel}</span>
                    <p className="font-serif text-2xl md:text-3xl text-[#3E362E] mt-2">
                      {w.nextDate}
                    </p>
                  </div>
                )}
                <div className="space-y-3 border-t border-[#CEBFAA] pt-5">
                  <MetaLine icon={<Clock size={18} />} value={w.time} />
                  <MetaLine icon={<MapPin size={18} />} value={w.place} />
                </div>
                <div className="border-t border-[#CEBFAA] pt-5">
                  <span className="overline">{w.priceLabel}</span>
                  <p className="font-serif text-4xl md:text-5xl text-[#3E362E] mt-2">
                    {w.price}
                  </p>
                </div>
                <div className="mt-auto grid grid-cols-1 gap-3">
                  <PaypalLink
                    amount="40.00"
                    item="Taller Regresion Grupal - Danwei Huang"
                    label={w.cta}
                    testId="workshop-cta-pay"
                  />
                  <WhatsappLink
                    message={w.whatsappMessage}
                    label={w.ctaWhatsapp}
                    testId="workshop-cta-whatsapp"
                    variant="secondary"
                  />
                </div>
              </div>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-12">
            <div className="lg:col-span-6">
              <Reveal>
                <h3 className="font-serif text-2xl md:text-3xl text-[#3E362E] mb-6">
                  {w.benefitsTitle}
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  {w.benefits.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-3 text-[#5E554C] text-base font-light"
                    >
                      <Check size={16} className="mt-1 text-[#1A9BC1] shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <div className="lg:col-span-6">
              <Reveal delay={150}>
                <h3 className="font-serif text-2xl md:text-3xl text-[#3E362E] mb-6">
                  {w.agendaTitle}
                </h3>
                <ol className="space-y-3 border-l border-[#1A9BC1]/40 pl-6">
                  {w.agenda.map((a, i) => (
                    <li
                      key={a}
                      className="text-[#5E554C] text-base font-light leading-relaxed"
                    >
                      <span className="font-serif text-[#1A9BC1] italic mr-3">{i + 1}.</span>
                      {a}
                    </li>
                  ))}
                </ol>
                <p className="mt-6 font-serif italic text-[#5E554C] text-base leading-relaxed">
                  {w.agendaNote}
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-16">
            <div className="lg:col-span-6">
              <Reveal>
                <h3 className="font-serif text-2xl md:text-3xl text-[#3E362E] mb-6">
                  {w.bringTitle}
                </h3>
                <ul className="space-y-3">
                  {w.bring.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-3 text-[#5E554C] text-base font-light"
                    >
                      <Check size={16} className="mt-1 text-[#1A9BC1] shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <div className="lg:col-span-6">
              <Reveal delay={150}>
                <div className="bg-[#F5EFE6] border border-[#E8E1D9] p-7 md:p-9 h-full">
                  <h3 className="font-serif text-2xl md:text-3xl text-[#3E362E] mb-4">
                    {w.forWhomTitle}
                  </h3>
                  <p className="text-[#5E554C] text-base font-light leading-relaxed">
                    {w.forWhom}
                  </p>
                  <p className="mt-6 font-serif italic text-lg text-[#1A9BC1]">
                    {w.intent}
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
